import { supabase } from '../config/supabase';
import { Learner, LearnerWithProgress, LearnerStatus, LearnerFilters } from '../types';

// Helper to convert DB row to Learner type
const toLearner = (row: Record<string, unknown>): Learner => ({
  id: row.id as string,
  organizationId: row.organization_id as string,
  structureId: row.structure_id as string | undefined,
  lmsUserId: row.lms_user_id as string | undefined,
  email: row.email as string,
  fullname: row.fullname as string,
  jobTitle: row.job_title as string | undefined,
  department: row.department as string | undefined,
  status: row.status as LearnerStatus,
  enrolledAt: row.enrolled_at ? new Date(row.enrolled_at as string) : undefined,
  completedAt: row.completed_at ? new Date(row.completed_at as string) : undefined,
  createdAt: new Date(row.created_at as string),
  updatedAt: new Date(row.updated_at as string)
});

export const learnersService = {
  // Get all learners (super_admin) or organization's learners (client)
  async getAll(filters?: LearnerFilters): Promise<Learner[]> {
    let query = supabase
      .from('learners')
      .select('*')
      .order('fullname');

    if (filters?.organizationId) {
      query = query.eq('organization_id', filters.organizationId);
    }

    if (filters?.structureId) {
      query = query.eq('structure_id', filters.structureId);
    }

    if (filters?.status) {
      query = query.eq('status', filters.status);
    }

    if (filters?.search) {
      query = query.or(`fullname.ilike.%${filters.search}%,email.ilike.%${filters.search}%`);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching learners:', error);
      throw new Error('Impossible de charger les apprenants');
    }

    return (data || []).map(toLearner);
  },

  // Get learners by structure with computed progress
  async getByStructure(structureId: string): Promise<LearnerWithProgress[]> {
    const { data: learners, error } = await supabase
      .from('learners')
      .select('*')
      .eq('structure_id', structureId)
      .order('fullname');

    if (error) {
      console.error('Error fetching learners:', error);
      throw new Error('Impossible de charger les apprenants');
    }

    // For each learner, calculate overall progress from enrollments
    const learnersWithProgress: LearnerWithProgress[] = await Promise.all(
      (learners || []).map(async (learnerRow) => {
        const learner = toLearner(learnerRow);

        const { data: enrollments } = await supabase
          .from('enrollments')
          .select('progress_percent')
          .eq('learner_id', learner.id);

        const overallProgress = enrollments && enrollments.length > 0
          ? Math.round(enrollments.reduce((sum, e) => sum + (e.progress_percent || 0), 0) / enrollments.length)
          : 0;

        return {
          ...learner,
          overallProgress
        };
      })
    );

    return learnersWithProgress;
  },

  // Get learners by external client ID (numéro client)
  async getByExternalClientId(externalClientId: string): Promise<LearnerWithProgress[]> {
    // First find the structure with this external client ID
    const { data: structure, error: structError } = await supabase
      .from('structures')
      .select('id')
      .eq('external_client_id', externalClientId)
      .maybeSingle();

    if (structError) {
      console.error('Error finding structure:', structError);
      throw new Error('Impossible de trouver la structure');
    }

    if (!structure) {
      return [];
    }

    return this.getByStructure(structure.id);
  },

  // Get learners by organization with computed progress and enrollments
  async getByOrganization(organizationId: string): Promise<LearnerWithProgress[]> {
    const { data: learners, error } = await supabase
      .from('learners')
      .select('*')
      .eq('organization_id', organizationId)
      .order('fullname');

    if (error) {
      console.error('Error fetching learners:', error);
      throw new Error('Impossible de charger les apprenants');
    }

    // For each learner, get enrollments with program details
    const learnersWithProgress: LearnerWithProgress[] = await Promise.all(
      (learners || []).map(async (learnerRow) => {
        const learner = toLearner(learnerRow);

        // Get enrollments with program details for this learner
        const { data: enrollments } = await supabase
          .from('enrollments')
          .select(`
            *,
            program:training_programs(*)
          `)
          .eq('learner_id', learner.id);

        const overallProgress = enrollments && enrollments.length > 0
          ? Math.round(enrollments.reduce((sum, e) => sum + (e.progress_percent || 0), 0) / enrollments.length)
          : 0;

        return {
          ...learner,
          enrollments: enrollments?.map(e => ({
            id: e.id,
            learnerId: e.learner_id,
            programId: e.program_id,
            enrolledAt: new Date(e.enrolled_at),
            dueDate: e.due_date ? new Date(e.due_date) : undefined,
            completedAt: e.completed_at ? new Date(e.completed_at) : undefined,
            progressPercent: e.progress_percent || 0,
            status: e.status,
            program: e.program ? {
              id: e.program.id,
              organizationId: e.program.organization_id,
              name: e.program.name,
              description: e.program.description,
              estimatedDurationHours: e.program.estimated_duration_hours,
              isActive: e.program.is_active,
              createdAt: new Date(e.program.created_at),
              updatedAt: new Date(e.program.updated_at)
            } : undefined
          })) || [],
          overallProgress
        };
      })
    );

    return learnersWithProgress;
  },

  // Get single learner by ID
  async getById(id: string): Promise<Learner | null> {
    const { data, error } = await supabase
      .from('learners')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      console.error('Error fetching learner:', error);
      throw new Error('Impossible de charger l\'apprenant');
    }

    return data ? toLearner(data) : null;
  },

  // Get learner with full progress details
  async getWithProgress(id: string): Promise<LearnerWithProgress | null> {
    const { data: learnerRow, error } = await supabase
      .from('learners')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      console.error('Error fetching learner:', error);
      throw new Error('Impossible de charger l\'apprenant');
    }

    if (!learnerRow) return null;

    const learner = toLearner(learnerRow);

    // Get organization
    const { data: orgData } = await supabase
      .from('organizations')
      .select('*')
      .eq('id', learner.organizationId)
      .single();

    // Get enrollments with program details
    const { data: enrollments } = await supabase
      .from('enrollments')
      .select(`
        *,
        program:training_programs(*),
        course_progress(*)
      `)
      .eq('learner_id', id);

    const overallProgress = enrollments && enrollments.length > 0
      ? Math.round(enrollments.reduce((sum, e) => sum + (e.progress_percent || 0), 0) / enrollments.length)
      : 0;

    return {
      ...learner,
      organization: orgData ? {
        id: orgData.id,
        name: orgData.name,
        slug: orgData.slug,
        contactName: orgData.contact_name,
        contactEmail: orgData.contact_email,
        contactPhone: orgData.contact_phone,
        logoUrl: orgData.logo_url,
        address: orgData.address,
        notes: orgData.notes,
        status: orgData.status,
        contractStart: orgData.contract_start ? new Date(orgData.contract_start) : undefined,
        contractEnd: orgData.contract_end ? new Date(orgData.contract_end) : undefined,
        createdAt: new Date(orgData.created_at),
        updatedAt: new Date(orgData.updated_at)
      } : undefined,
      enrollments: enrollments?.map(e => ({
        id: e.id,
        learnerId: e.learner_id,
        programId: e.program_id,
        enrolledAt: new Date(e.enrolled_at),
        dueDate: e.due_date ? new Date(e.due_date) : undefined,
        completedAt: e.completed_at ? new Date(e.completed_at) : undefined,
        progressPercent: e.progress_percent || 0,
        status: e.status,
        program: e.program ? {
          id: e.program.id,
          organizationId: e.program.organization_id,
          name: e.program.name,
          description: e.program.description,
          estimatedDurationHours: e.program.estimated_duration_hours,
          isActive: e.program.is_active,
          createdAt: new Date(e.program.created_at),
          updatedAt: new Date(e.program.updated_at)
        } : undefined,
        courseProgress: e.course_progress?.map((cp: Record<string, unknown>) => ({
          id: cp.id,
          enrollmentId: cp.enrollment_id,
          courseId: cp.course_id,
          progressPercent: cp.progress_percent || 0,
          startedAt: cp.started_at ? new Date(cp.started_at as string) : undefined,
          completedAt: cp.completed_at ? new Date(cp.completed_at as string) : undefined,
          timeSpentMinutes: cp.time_spent_minutes || 0,
          lastActivityAt: cp.last_activity_at ? new Date(cp.last_activity_at as string) : undefined,
          lmsSyncAt: cp.lms_sync_at ? new Date(cp.lms_sync_at as string) : undefined
        })) || []
      })) || [],
      overallProgress
    };
  },

  // Create learner
  async create(learner: Omit<Learner, 'id' | 'createdAt' | 'updatedAt'>): Promise<Learner> {
    const insertData = {
      organization_id: learner.organizationId,
      structure_id: learner.structureId,
      lms_user_id: learner.lmsUserId,
      email: learner.email,
      fullname: learner.fullname,
      job_title: learner.jobTitle,
      department: learner.department,
      status: learner.status || 'active',
      enrolled_at: learner.enrolledAt?.toISOString(),
      completed_at: learner.completedAt?.toISOString()
    };

    const { data, error } = await supabase
      .from('learners')
      .insert(insertData)
      .select()
      .single();

    if (error) {
      console.error('Error creating learner:', error);
      if (error.code === '23505') {
        throw new Error('Un apprenant avec cet email existe déjà');
      }
      throw new Error('Impossible de créer l\'apprenant');
    }

    return toLearner(data);
  },

  // Update learner
  async update(id: string, updates: Partial<Omit<Learner, 'id' | 'createdAt' | 'updatedAt'>>): Promise<Learner> {
    const updateData: Record<string, unknown> = {
      updated_at: new Date().toISOString()
    };

    if (updates.organizationId !== undefined) updateData.organization_id = updates.organizationId;
    if (updates.structureId !== undefined) updateData.structure_id = updates.structureId;
    if (updates.lmsUserId !== undefined) updateData.lms_user_id = updates.lmsUserId;
    if (updates.email !== undefined) updateData.email = updates.email;
    if (updates.fullname !== undefined) updateData.fullname = updates.fullname;
    if (updates.jobTitle !== undefined) updateData.job_title = updates.jobTitle;
    if (updates.department !== undefined) updateData.department = updates.department;
    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.enrolledAt !== undefined) updateData.enrolled_at = updates.enrolledAt?.toISOString();
    if (updates.completedAt !== undefined) updateData.completed_at = updates.completedAt?.toISOString();

    const { data, error } = await supabase
      .from('learners')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating learner:', error);
      throw new Error('Impossible de mettre à jour l\'apprenant');
    }

    return toLearner(data);
  },

  // Delete learner
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('learners')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting learner:', error);
      throw new Error('Impossible de supprimer l\'apprenant');
    }
  },

  // Get learners count by status for an organization
  async getStatsByOrganization(organizationId: string): Promise<{
    total: number;
    active: number;
    completed: number;
    inactive: number;
  }> {
    const { data, error } = await supabase
      .from('learners')
      .select('status')
      .eq('organization_id', organizationId);

    if (error) {
      console.error('Error fetching learner stats:', error);
      throw new Error('Impossible de charger les statistiques');
    }

    const learners = data || [];
    return {
      total: learners.length,
      active: learners.filter(l => l.status === 'active').length,
      completed: learners.filter(l => l.status === 'completed').length,
      inactive: learners.filter(l => l.status === 'inactive').length
    };
  },

  // Search learners
  async search(query: string, organizationId?: string): Promise<Learner[]> {
    let dbQuery = supabase
      .from('learners')
      .select('*')
      .or(`fullname.ilike.%${query}%,email.ilike.%${query}%,department.ilike.%${query}%`);

    if (organizationId) {
      dbQuery = dbQuery.eq('organization_id', organizationId);
    }

    const { data, error } = await dbQuery.order('fullname');

    if (error) {
      console.error('Error searching learners:', error);
      throw new Error('Erreur lors de la recherche');
    }

    return (data || []).map(toLearner);
  },

  // Bulk import learners - handles duplicates by checking existing first, then creating new
  async bulkCreate(learners: Omit<Learner, 'id' | 'createdAt' | 'updatedAt'>[]): Promise<Learner[]> {
    if (learners.length === 0) return [];

    const results: Learner[] = [];

    // Process one by one to handle duplicates gracefully
    for (const learner of learners) {
      // First, check if learner already exists by email + organization_id
      const { data: existing } = await supabase
        .from('learners')
        .select('*')
        .eq('email', learner.email)
        .eq('organization_id', learner.organizationId)
        .single();

      if (existing) {
        // Learner already exists - return existing one (optionally update department/structure if provided)
        if (learner.department || learner.structureId) {
          const updateData: Record<string, unknown> = {};
          if (learner.department && !existing.department) {
            updateData.department = learner.department;
          }
          if (learner.structureId && !existing.structure_id) {
            updateData.structure_id = learner.structureId;
          }

          if (Object.keys(updateData).length > 0) {
            await supabase
              .from('learners')
              .update(updateData)
              .eq('id', existing.id);
          }
        }
        results.push(toLearner(existing));
        continue;
      }

      // Learner doesn't exist - create new one
      const insertData = {
        organization_id: learner.organizationId,
        structure_id: learner.structureId,
        lms_user_id: learner.lmsUserId,
        email: learner.email,
        fullname: learner.fullname,
        job_title: learner.jobTitle,
        department: learner.department,
        status: learner.status || 'active',
        enrolled_at: learner.enrolledAt?.toISOString(),
        completed_at: learner.completedAt?.toISOString()
      };

      const { data, error } = await supabase
        .from('learners')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        console.error('Error creating learner:', learner.email, error);
        // Continue with other learners instead of failing completely
        continue;
      }

      if (data) {
        results.push(toLearner(data));
      }
    }

    return results;
  },

  // Update learner status based on enrollment completion
  async updateStatusFromEnrollments(learnerId: string): Promise<void> {
    // Get all enrollments for this learner
    const { data: enrollments, error } = await supabase
      .from('enrollments')
      .select('status')
      .eq('learner_id', learnerId);

    if (error) {
      console.error('Error fetching enrollments:', error);
      return;
    }

    if (!enrollments || enrollments.length === 0) {
      return;
    }

    // Determine learner status based on enrollments
    const allCompleted = enrollments.every(e => e.status === 'completed');
    const hasInProgress = enrollments.some(e => e.status === 'in_progress');

    let newStatus: LearnerStatus = 'active';
    if (allCompleted) {
      newStatus = 'completed';
    } else if (hasInProgress) {
      newStatus = 'active';
    }

    await supabase
      .from('learners')
      .update({
        status: newStatus,
        completed_at: allCompleted ? new Date().toISOString() : null,
        updated_at: new Date().toISOString()
      })
      .eq('id', learnerId);
  }
};
